"use client";

import React, { useState, useEffect, useRef } from "react";
import { TiTimes } from "react-icons/ti";
import { useOutsideClick } from "../../hooks/useOutsideClick";

export interface FontOption {
  label: string;
  value: string;
  googleFont?: string;
}

export interface SelectProps {
  options: FontOption[];
  value?: FontOption;
  onChange?: (option?: FontOption) => void;
  label?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;

  inputW?: number;
  borderStyle?: string;
  noBorder?: boolean;
  className?: string;
  clickable?: boolean;
  onClick?: () => void;
  dropdownStyle?: string;
  dropdownItemStyle?: string;
  inputParentStyle?: string;
  cancelStyle?: string;
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  label,
  name,
  required = false,
  placeholder = "Select...",
  inputW,
  borderStyle,
  noBorder = false,
  className = "",
  clickable = false,
  onClick,
  dropdownStyle = "",
  dropdownItemStyle = "",
  inputParentStyle = "",
  cancelStyle = "",
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<FontOption | undefined>(value);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useOutsideClick(dropdownRef, () => setIsDropdownOpen(false));

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option: FontOption) => {
    setSelected(option);
    setSearchTerm("");
    setIsDropdownOpen(false);
    onChange?.(option);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSelected(undefined);
    setIsDropdownOpen(true);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    setSearchTerm("");
    setSelected(undefined);
    setIsDropdownOpen(false);
    onChange?.(undefined);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleFocus = () => {
    if (!selected && !searchTerm) {
      setIsDropdownOpen(true);
    }
  };

  return (
    <div
      className={`relative max-w-[400px] h-full ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="block mb-1 font-medium text-sm text-gray-700">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative h-full">
        <div
          className={`flex bg-secondary-main items-center shadow-sm text-sm appearance-none ${
            noBorder ? "border-none" : "border border-label-main"
          } rounded-md  text-secondary-main p-2 py-2 leading-tight focus:outline-none ${inputParentStyle} ${borderStyle}`}
          tabIndex={0}
          aria-expanded={isDropdownOpen}
        >
          <SearchIcon />
          <input
            ref={inputRef}
            name={name}
            type="text"
            style={{ width: inputW }}
            placeholder={placeholder}
            className="placeholder:text-sm bg-transparent text-white placeholder:font-normal text-sm w-full p-1 h-full outline-none"
            value={searchTerm || selected?.label || ""}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onClick={onClick}
            readOnly={!!selected || clickable}
          />
          <div className="flex items-center ml-2">
            {selected ? (
              <button
                type="button"
                onClick={(e) => {
                  onClick?.();
                  handleClear(e);
                }}
                className={`flex  items-center justify-center bg-black text-white border border-label-main size-4 rounded-full ${cancelStyle}`}
              >
                <TiTimes size={12} />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  onClick?.();
                  toggleDropdown();
                }}
                className="ml-1"
              >
                <ArrowDown
                  className={`transition-transform duration-300 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            )}
          </div>
        </div>

        {isDropdownOpen && (
          <ul
            className={`absolute z-20 scrollbar-hide bg-gray-900 text-white border rounded shadow-lg max-h-48 w-[200px] right-0 overflow-auto mt-1 ${dropdownStyle}`}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={index}
                  className={`px-4 text-sm py-2 hover:bg-gray-200 hover:text-gray-900 capitalize cursor-pointer ${dropdownItemStyle}`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-sm text-gray-500">
                No matches found
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Select;

const ArrowDown: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <svg
      className={className}
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.33333 8.48181L10.6667 12.4818L14 8.48181"
        stroke="#F7F9FE"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

const SearchIcon = () => {
  return (
    <svg
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.28156 13.9434C5.68956 13.9434 3.60156 11.8554 3.60156 9.26337C3.60156 6.67137 5.68956 4.58337 8.28156 4.58337C10.8736 4.58337 12.9616 6.67137 12.9616 9.26337C12.9616 11.8554 10.8736 13.9434 8.28156 13.9434ZM8.28156 5.30337C6.08556 5.30337 4.32156 7.06737 4.32156 9.26337C4.32156 11.4594 6.08556 13.2234 8.28156 13.2234C10.4776 13.2234 12.2416 11.4594 12.2416 9.26337C12.2416 7.06737 10.4776 5.30337 8.28156 5.30337Z"
        fill="white"
      />
      <path
        d="M11.7669 12.2396L14.9997 15.4724L14.4906 15.9815L11.2578 12.7487L11.7669 12.2396Z"
        fill="white"
      />
    </svg>
  );
};
